
import { PayableStatus } from "@/types/payable";
import { InvoiceStatus, ReceivableStatus, StatusBadge } from "@/types/payments";

// Helper to get status badge variant and label
export const getStatusBadge = (status: InvoiceStatus | ReceivableStatus | PayableStatus): StatusBadge => {
  const statusMap = {
    unpaid: {
      variant: "unpaid" as const,
      label: "Unpaid"
    },
    overdue: {
      variant: "overdue" as const,
      label: "Overdue"
    },
    pending: {
      variant: "pending" as const,
      label: "Pending"
    },
    paid: {
      variant: "paid" as const,
      label: "Paid"
    },
    manually_handled: {
      variant: "secondary" as const,
      label: "Externally Handled"
    },
    failed: {
      variant: "failed" as const,
      label: "Failed"
    },
    cancelled: {
      variant: "destructive" as const,
      label: "Cancelled"
    },
    action_required: {
      variant: "outline" as const,
      label: "Action Needed"
    }
  };
  
  return statusMap[status];
};

export interface PaymentVerificationDisplay {
  /** Short label for a compact row-level badge. */
  label: string;
  /** Plain-language explanation for a tooltip/detail view (design spec's UX Requirements — the
   * business owner error state explicitly calls for prose, "not just a code or icon"). */
  description: string;
}

// Normalize common non-ISO currency abbreviations to ISO 4217 codes
const normalizeCurrency = (currency: string): string => {
  const normalized = currency.trim().toUpperCase();
  const currencyMap: Record<string, string> = {
    'KR': 'SEK',
    'SEK': 'SEK',
    'NOK': 'NOK',
    'DKK': 'DKK',
    'EUR': 'EUR',
    'USD': 'USD',
    'GBP': 'GBP',
  };
  return currencyMap[normalized] || normalized;
};

/** Shown for a document that has no amount yet (an unextracted draft) — never "0.00". */
const AMOUNT_UNKNOWN = '—';

/** The plain "1250.5 SEK" form, or the same dash as formatCurrency when there is no amount. */
export const formatPlainAmount = (amount: number | null | undefined, currency: string) =>
  amount === null || amount === undefined ? AMOUNT_UNKNOWN : `${amount} ${currency}`;

/** The consultant app's "SEK 1,234.00" code-first amount shape, which differs from `formatAmount`'s Swedish one and is not changed by the locale work. */
const CODE_AMOUNT_LOCALE = 'en-US';

// Format currency
export const formatCurrency =(amount: number | null | undefined, currency: string = 'USD') => {
  if (amount === null || amount === undefined) return AMOUNT_UNKNOWN;
  const isoCurrency = normalizeCurrency(currency);
  try {
    return new Intl.NumberFormat(CODE_AMOUNT_LOCALE, {
      style: 'currency',
      currency: isoCurrency,
      currencyDisplay: 'code'
    }).format(amount);
  } catch {
    // Fallback if currency code is still invalid
    return `${amount.toFixed(2)} ${isoCurrency}`;
  }
};
